import { AlertCircle, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { ItemForm, type ItemFormSubmitValues } from '@/components/item-form'
import { PageHeader } from '@/components/page-header'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { trpc } from '@/trpc/client'

export function EditItemPage() {
	const { id = '' } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const utils = trpc.useUtils()
	const [error, setError] = useState<string | null>(null)

	const itemQuery = trpc.items.get.useQuery({ id }, { enabled: id !== '' })
	const update = trpc.items.update.useMutation()

	async function handleSubmit(values: ItemFormSubmitValues) {
		setError(null)
		try {
			await update.mutateAsync({
				id,
				name: values.name,
				price: values.price,
				currency: values.currency,
				purchaseDate: values.purchaseDate,
				warrantyMonths: values.warrantyMonths,
				notifyBeforeExpiry: values.notifyBeforeExpiry,
				notifyDaysBefore: values.notifyDaysBefore,
				store: values.store,
				notes: values.notes,
				categoryIds: values.categoryIds,
				...(values.file ?? {}),
			})
			await Promise.all([
				utils.items.list.invalidate(),
				utils.items.get.invalidate({ id }),
			])
			toast.success('Changes saved')
			navigate(`/app/items/${id}`)
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not save changes')
		}
	}

	if (itemQuery.isPending) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-10 w-48" />
				<Skeleton className="h-64 w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}

	if (itemQuery.isError) {
		return (
			<Alert variant="destructive">
				<AlertCircle />
				<AlertTitle>Couldn't load this item</AlertTitle>
				<AlertDescription>
					{itemQuery.error.message}
					<div className="mt-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => itemQuery.refetch()}
						>
							<RefreshCw className="size-4" />
							Retry
						</Button>
					</div>
				</AlertDescription>
			</Alert>
		)
	}

	const item = itemQuery.data

	return (
		<div className="space-y-8">
			<PageHeader
				title="Edit item"
				description={item.name}
				actions={
					<Button asChild variant="ghost">
						<Link to={`/app/items/${id}`}>Back to item</Link>
					</Button>
				}
			/>
			<ItemForm
				key={item.id}
				defaultValues={{
					name: item.name,
					price: String(item.price),
					currency: item.currency,
					purchaseDate: new Date(item.purchaseDate).toISOString().slice(0, 10),
					warrantyMonths:
						item.warrantyMonths == null ? '' : String(item.warrantyMonths),
					notifyBeforeExpiry: item.notifyBeforeExpiry,
					notifyDaysBefore: String(item.notifyDaysBefore),
					store: item.store ?? '',
					notes: item.notes ?? '',
					categoryIds: item.categories.map((c) => c.id),
					file: item.fileKey
						? {
								fileKey: item.fileKey,
								fileMimeType:
									(item.fileMimeType as
										| 'image/jpeg'
										| 'image/png'
										| 'image/webp'
										| 'application/pdf') ?? 'application/pdf',
								fileSizeBytes: item.fileSizeBytes ?? 0,
								fileOriginalName: item.fileOriginalName ?? 'file',
							}
						: null,
				}}
				submitLabel="Save changes"
				onSubmit={handleSubmit}
				pending={update.isPending}
				error={error}
				onCancel={() => navigate(`/app/items/${id}`)}
			/>
		</div>
	)
}
